import type React from 'react';

import { Alert, Badge, Button, Image, Text } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';
// import { useTracking } from '@change-corgi/core/react/tracking';

import endorsementsBannerImage2x from 'src/assets/starterDashboardV2/endorsements/banner-image-2x.png';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { useDashboardEndorsements } from '../../../../../../hooks/useDashboardEndorsements';

import { useEndorsementsBanner } from './hooks/useEndorsementsBanner';

const MINIMUM_SIGNATURES_TO_SHOW_BANNER = 100;

export const EndorsementsBanner: React.FC = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const { translate } = useI18n();
	// const track = useTracking();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const isEndorsementsEnabled = useDashboardEndorsements();
	const { data, actions } = useEndorsementsBanner();

	const signatureCount = petition.signatureState.signatureCount.displayed;
	const hasMinimumSignatures = signatureCount > MINIMUM_SIGNATURES_TO_SHOW_BANNER;

	if (!isEndorsementsEnabled.data.isEnabled || data.isDismissed || !hasMinimumSignatures) {
		return null;
	}

	const endorsementAddUrl = `/p/${slug}/dashboard/endorsements/add`;

	return (
		<div className="p-4 pt-2.5 pb-0 lg:p-6">
			<Alert
				className="h-full w-full [&_button]:cursor-pointer [&>div>div>:first-child]:hidden"
				containerClassName="[&>div]:flex-row [&>div]:items-center [&>div>div:last-child]:flex [&>div>div:last-child]:flex-row [&>div>div:last-child]:items-center [&>div>div:last-child]:gap-6 [&>div>div:last-child]:w-full"
				variant="info"
				onDismissClick={actions.handleDismiss}
				dismissButtonAriaLabel={translate(
					'corgi.petition.starter_dashboard_v2.endorsements.banner.dismiss_button_aria_label',
				)}
				data-testid="endorsements-banner"
				data-qa="endorsements-banner"
			>
				<div className="flex w-full flex-col gap-8 lg:flex-row lg:items-center lg:gap-6">
					<div className="flex flex-1 flex-col gap-5">
						<div className="flex flex-col-reverse gap-3 lg:flex-row lg:items-center lg:gap-2">
							<Text variant="body" size="large" fontWeight="bold">
								<Translate value="corgi.petition.starter_dashboard_v2.endorsements.banner.heading" />
							</Text>
							<Badge
								label={<Translate value="corgi.petition.starter_dashboard_v2.endorsements.banner.badge" />}
								size="tiny"
								variant="information"
								emphasis="strong"
								data-testid="endorsements-banner-badge"
								data-qa="endorsements-banner-badge"
								className="w-fit"
							/>
						</div>
						<Text as="p">
							<Translate value="corgi.petition.starter_dashboard_v2.endorsements.banner.body" />
						</Text>
						<Button
							as="a"
							className="w-full text-center lg:w-fit"
							variant="secondary-solid"
							size="x-small"
							href={endorsementAddUrl}
							target="_blank"
							rel="noopener noreferrer"
							data-testid="add-endorsement-button"
							data-qa="add-endorsement-button"
						>
							<Translate value="corgi.petition.starter_dashboard_v2.endorsements.banner.add_button" />
						</Button>
					</div>
					<div className="flex shrink-0 justify-center lg:mr-4">
						<Image
							src={endorsementsBannerImage2x}
							alt="Endorsements banner illustration"
							className="h-48 w-64 object-contain"
							data-testid="endorsements-banner-image"
							data-qa="endorsements-banner-image"
						/>
					</div>
				</div>
			</Alert>
		</div>
	);
};
